import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { getCharacter } from "@/lib/characters";
import { Lock, Check, Crown, BadgeCheck } from "lucide-react";

export default function Shop() {
  const { user, refresh } = useAuth();
  const [catalog, setCatalog] = useState({ characters: [], badges: [] });
  const [wallet, setWallet] = useState({ balance_usdt: 0 });
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.get("/shop/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  };
  useEffect(load, []);

  const buy = async (kind, itemId) => {
    setBusy(itemId);
    try {
      const { data } = await api.post("/shop/buy", { kind, item_id: itemId });
      setWallet((w) => ({ ...w, balance_usdt: data.balance_usdt }));
      toast.success(`Purchased for ${data.price} USDT`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Purchase failed"));
    } finally {
      setBusy(null);
    }
  };

  const equip = async (characterId) => {
    setBusy(characterId);
    try {
      await api.patch("/auth/avatar", { character_id: characterId });
      toast.success("Character equipped");
      refresh?.();
    } catch (err) {
      toast.error(apiError(err, "Equip failed"));
    } finally {
      setBusy(null);
    }
  };

  const equippedId = user?.avatar?.character_id;

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8" data-testid="shop-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="label-mono mb-2 text-brand-magenta">// grid_market</div>
          <h1 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">THE SHOP.</h1>
          <p className="text-white/50 font-body mt-1">Characters, badges and passes — all yours for USDT.</p>
        </div>
        <div className="panel px-5 py-3" data-testid="shop-wallet-mini">
          <span className="label-mono mr-3">Wallet</span>
          <span className="font-mono text-xl font-bold text-neon-green glow-green">{wallet.balance_usdt?.toFixed(2)} USDT</span>
        </div>
      </div>

      <section className="mb-12">
        <div className="label-mono mb-4">// 01_characters</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="shop-characters-grid">
          {catalog.characters.map((item) => {
            const c = getCharacter(item.id);
            const equipped = equippedId === item.id;
            return (
              <div key={item.id} className={`panel overflow-hidden group ${equipped ? "border-neon-green" : ""}`} data-testid={`shop-character-${item.id}`}>
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img src={c.img} alt={c.name} className={`w-full h-full object-cover transition-transform group-hover:scale-105 ${!item.owned ? "grayscale-[40%]" : ""}`} loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                  <div className="absolute top-2 right-2">
                    {item.owned ? (
                      <span className="label-mono text-neon-green bg-black/70 border border-neon-green/50 px-2 py-1 flex items-center gap-1"><Check className="w-3 h-3" /> OWNED</span>
                    ) : (
                      <span className="label-mono text-brand-amber bg-black/70 border border-brand-amber/50 px-2 py-1 flex items-center gap-1"><Lock className="w-3 h-3" /> {item.price} USDT</span>
                    )}
                  </div>
                  <div className="absolute bottom-2 left-3 right-3">
                    <div className="font-display font-bold">{c.name}</div>
                    <div className="text-xs font-mono" style={{ color: `#${c.accent}` }}>{c.role}</div>
                  </div>
                </div>
                <div className="p-3">
                  {item.owned ? (
                    equipped ? (
                      <div className="w-full text-center label-mono text-neon-green py-2 border border-neon-green/40">EQUIPPED</div>
                    ) : (
                      <button onClick={() => equip(item.id)} disabled={busy === item.id} className="btn-neon w-full !py-2 text-xs" data-testid={`equip-${item.id}`}>EQUIP</button>
                    )
                  ) : (
                    <button onClick={() => buy("character", item.id)} disabled={busy === item.id} className="btn-ghost w-full !py-2 text-xs" data-testid={`buy-character-${item.id}`}>
                      {busy === item.id ? "…" : `BUY · ${item.price} USDT`}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="mb-12">
        <div className="label-mono mb-4">// 02_badges</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="shop-badges-grid">
          {catalog.badges.map((b) => (
            <div key={b.id} className="panel p-4 text-center" data-testid={`shop-badge-${b.id}`}>
              <div className="w-14 h-14 mx-auto rounded-full grid place-items-center border-2 mb-3" style={{ borderColor: `#${b.color}`, boxShadow: `0 0 18px #${b.color}44` }}>
                <BadgeCheck className="w-6 h-6" style={{ color: `#${b.color}` }} />
              </div>
              <div className="font-display font-bold text-sm">{b.name}</div>
              <p className="text-white/40 text-[11px] font-body mt-1 mb-3">{b.desc}</p>
              {b.owned ? (
                <div className="label-mono text-neon-green py-1.5 border border-neon-green/40">OWNED</div>
              ) : (
                <button onClick={() => buy("badge", b.id)} disabled={busy === b.id} className="btn-ghost w-full !py-1.5 text-xs" data-testid={`buy-badge-${b.id}`}>
                  {busy === b.id ? "…" : `${b.price} USDT`}
                </button>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="label-mono mb-4">// 03_passes</div>
        <Link to="/membership" className="panel panel-glow p-6 flex items-center justify-between group hover:-translate-y-0.5 transition-transform" data-testid="shop-passes-link">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 border-2 border-brand-amber grid place-items-center">
              <Crown className="w-6 h-6 text-brand-amber" />
            </div>
            <div>
              <div className="font-display font-black text-xl">MEMBERSHIP PASSES</div>
              <p className="text-white/50 text-sm font-body">Premium & Gold — lower fees, weekly cashback, VIP perks.</p>
            </div>
          </div>
          <span className="label-mono text-brand-amber group-hover:translate-x-1 transition-transform">view tiers →</span>
        </Link>
      </section>
    </div>
  );
}
